export type PacingStage={level:number;bpm:number;durationSeconds:number};
export type PacedBeat={level:number;bpm:number;beat:number;elapsedSeconds:number};
export type ContinuousTermination='completed'|'examiner-ended'|'audio-interrupted'|'pacing-interrupted'|'audio-unavailable';
export type ContinuousMetronomeOptions={stages:readonly PacingStage[];onBeat?:(beat:PacedBeat)=>void;onEnd:(reason:ContinuousTermination,elapsedSeconds:number)=>void};
export type ContinuousMetronomeRun={elapsedSeconds:()=>number;end:()=>void;cancel:()=>void;interrupt:()=>void};
export const PACING_LOOKAHEAD_SECONDS=0.1;
export const PACING_POLL_MS=25;
export const PACING_START_LEAD_SECONDS=0.05;

export function pacingTimeline(stages:readonly PacingStage[]) {
  const beats:PacedBeat[]=[];let elapsed=0,index=0;
  for(const stage of stages) {
    const count=stage.durationSeconds*stage.bpm/60;
    if(!Number.isFinite(stage.durationSeconds)||stage.durationSeconds<=0||!Number.isFinite(stage.bpm)||stage.bpm<=0||!Number.isInteger(count)) throw new Error('The continuous pacing schedule is invalid.');
    for(let n=0;n<count;n++) beats.push({level:stage.level,bpm:stage.bpm,beat:index++%4,elapsedSeconds:elapsed+n*60/stage.bpm});
    elapsed+=stage.durationSeconds;
  }
  if(!beats.length)throw new Error('A continuous pacing schedule is required.');
  return {beats,durationSeconds:elapsed};
}

type Environment={
  now:()=>number;
  isAudioRunning:()=>boolean;
  scheduleBeat:(beat:PacedBeat,audioTime:number)=>void;
  cancelTones:()=>void;
  setTimer:(callback:()=>void,delayMs:number)=>number;
  clearTimer:(id:number)=>void;
};
/** One audio-clock origin drives every beat; no stage boundary restarts a timer. */
export function createContinuousPacing(options:ContinuousMetronomeOptions,environment:Environment) {
  const timeline=pacingTimeline(options.stages),timers=new Set<number>();
  let origin=0,next=0,started=false,finished=false,finalElapsed=0;
  function elapsed() {
    if(!started)return 0;
    if(finished)return finalElapsed;
    // Never credit time after the first tone that has not actually been scheduled.
    return Math.max(0,Math.min(environment.now()-origin,timeline.beats[next]?.elapsedSeconds??timeline.durationSeconds,timeline.durationSeconds));
  }
  function cancel() {
    if(finished)return;
    finalElapsed=elapsed();finished=true;
    for(const timer of timers)environment.clearTimer(timer);
    timers.clear();environment.cancelTones();
  }
  function finish(reason:ContinuousTermination,at=elapsed()) {
    if(finished)return;
    cancel();finalElapsed=at;
    options.onEnd(reason,at);
  }
  function defer(callback:()=>void,delayMs:number) {
    const id=environment.setTimer(()=>{timers.delete(id);if(!finished)callback();},Math.max(0,delayMs));timers.add(id);
  }
  function schedule() {
    if(finished)return;
    if(!environment.isAudioRunning()){finish('audio-interrupted');return;}
    const now=environment.now(),candidate=timeline.beats[next];
    // Check this before automatic completion: a long main-thread stall must not
    // claim stages beyond the first missed beat, even when now is past ten minutes.
    if(candidate&&origin+candidate.elapsedSeconds<now){finish('pacing-interrupted',candidate.elapsedSeconds);return;}
    if(now>=origin+timeline.durationSeconds){finish('completed',timeline.durationSeconds);return;}
    while(next<timeline.beats.length) {
      const beat=timeline.beats[next],at=origin+beat.elapsedSeconds;
      if(at>=now+PACING_LOOKAHEAD_SECONDS)break;
      try {environment.scheduleBeat(beat,at);}catch{finish('audio-unavailable');return;}
      next++;
      if(options.onBeat)defer(()=>options.onBeat?.(beat),(at-now)*1000);
    }
    defer(schedule,PACING_POLL_MS);
  }
  return {
    start(){if(started||finished)return;started=true;origin=environment.now()+PACING_START_LEAD_SECONDS;schedule();},
    elapsedSeconds:elapsed,
    end(){finish('examiner-ended');},
    interrupt(){finish('audio-interrupted');},
    cancel,
  };
}
